import { t } from '@lingui/core/macro';

import {
  type ImpactScoreMetricType,
  type ImpactScoreWeight,
  type ImpactScoreWeightSettings,
} from '@/api/settings';
import {
  NumberInput,
  NumberInputGroup,
  NumberInputInput,
  NumberInputTrigger,
  useTypedAppFormContext,
} from '@/components/Form';
import { Field, FieldLabel } from '@/components/Form/fields';

export type ResolvedImpactScoreWeight = ImpactScoreWeight & {
  fieldName: `weights[${number}].weight`;
};

type Props = {
  metricType: ImpactScoreMetricType;
  weights: ResolvedImpactScoreWeight[];
  disabled: boolean;
};

const getMetricTypeLabel = (metricType: ImpactScoreMetricType) => {
  switch (metricType) {
    case 'digital_metrics':
      return t`Digital Engagement`;
    case 'pipeline_metrics':
      return t`Pipeline`;
    case 'offline_campaign_metrics':
      return t`Campaign Engagement (Custom)`;
    case 'sales_activity_metrics':
      return t`Sales Activity`;
  }
};

const normalizeWeight = (weight: number | null) => {
  if (weight == null || !Number.isFinite(weight)) {
    return 0;
  }

  const clampedWeight = Math.min(100, Math.max(0, weight));
  return Math.round(clampedWeight * 100) / 100;
};

const CompanyImpactScoreMetricTypeSection = ({ metricType, weights, disabled }: Props) => {
  const form = useTypedAppFormContext({ defaultValues: {} as ImpactScoreWeightSettings });

  return (
    <section className="flex flex-col gap-4">
      <FieldLabel className="text-base leading-5 font-semibold tracking-[-0.03em] text-gray-900">
        {getMetricTypeLabel(metricType)}
      </FieldLabel>
      <div className="flex flex-col gap-3">
        {weights.map((weight) => (
          <Field
            key={weight.id}
            orientation="horizontal"
            className="items-center justify-between gap-4"
          >
            <FieldLabel
              htmlFor={`impact-score-weight-${weight.id}`}
              className="min-w-0 flex-1 text-sm leading-5 font-normal tracking-normal"
            >
              {weight.metricName}
            </FieldLabel>
            <form.AppField name={weight.fieldName}>
              {(field) => (
                <NumberInput
                  className="w-27.5 max-w-27.5 shrink-0"
                  size="medium"
                  value={field.state.value}
                  min={0}
                  max={100}
                  step={0.01}
                  disabled={disabled}
                  onValueChange={(value) => field.handleChange(normalizeWeight(value))}
                >
                  <NumberInputGroup>
                    <NumberInputInput
                      id={`impact-score-weight-${weight.id}`}
                      aria-label={t`${weight.metricName} weight`}
                      onBlur={field.handleBlur}
                    />
                    <NumberInputTrigger />
                  </NumberInputGroup>
                </NumberInput>
              )}
            </form.AppField>
          </Field>
        ))}
      </div>
    </section>
  );
};

export default CompanyImpactScoreMetricTypeSection;
